<template>
  <Dialog
    id="export-sizing-dialog"
    :visible="shouldExportSizing"
    :closable="!isDownloading"
    :dismissable-mask="!isDownloading"
    :close-on-escape="!isDownloading"
    :breakpoints="{ '1180px': '75vw', '640px': '100vw' }"
    :style="{ width: '50vw' }"
    header="Export Sizing to CSV/XLSX"
    :draggable="false"
    data-cy="export-sizing-dialog"
    @update:visible="emit('update:shouldExportSizing', false)"
  >
    <h2 class="mt-0" data-cy="file-type-header">File Type</h2>
    <!-- append-to on these dropdowns must be set to self and not body else they do not appear -->
    <Dropdown
      v-model="exportOptions.filetype"
      append-to="self"
      :options="['XLSX', 'CSV']"
      data-cy="file-type-dropdown"
    />
    <h2 data-cy="default-units-header">Default Units</h2>
    <div class="grid" data-cy="units-grid">
      <div v-for="(col, i) in dropdownOptions" :key="i" class="col-12 md:col-6">
        <div
          v-for="dd in col"
          :key="dd.label"
          class="grid align-items-center py-2"
          :class="{ 'pl-2': i == 1 }"
          :data-cy="'unit-section-' + dd.label"
        >
          <span class="col-6 text-right pr-3">{{ dd.label }}</span>
          <Dropdown
            v-model="exportOptions[dd.selectedUnit]"
            class="col-6"
            option-label="label"
            datakey="label"
            :options="dd.unitsAvailable"
            append-to="self"
            :data-cy="'unit-dropdown-' + dd.label"
          />
        </div>
      </div>
    </div>
    <br />
    <br />
    <br />
    <br />
    <br />
    <br />
    <template #footer>
      <div class="text-right my-3">
        <ProgressSpinner
          v-if="isDownloading"
          style="width: 50px; height: 50px"
          strokeWidth="8"
          fill="transparent"
          animationDuration=".5s"
          aria-label="Custom ProgressSpinner"
        />
        <p v-if="!isDownloading">
          <Button
            class="p-button-info"
            data-cy="download-button"
            @click="formatExport"
          >
            Download
          </Button>
        </p>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
/**
 * Xlsx mini build is untyped so ts-ignore is added to satisfy compiler
 * https://github.com/SheetJS/sheetjs/issues/1999
 *
 * Mini build is used because it is 212KB vs 1.2MB
 * for the whole build
 */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import XLSX from "xlsx/dist/xlsx.mini.min.js";
import type {
  OperatingParameters,
  Sizing,
  WellProperties,
  XrefPumps
} from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import { useSizingUnits } from "@/composables/units/useSizingUnits";
import type { DestructuredScenario } from "@/composables/useDestructureScenario";
import useDestructureScenario from "@/composables/useDestructureScenario";
import { mapKeys } from "@/utils/utils";
import Dialog from "primevue/dialog";
import type { PropType } from "vue";
import { ref, computed, reactive, watchEffect, unref, nextTick } from "vue";
import type { Sheet } from "xlsx";
import ProgressSpinner from "primevue/progressspinner";
import { getExportSections } from "./ExportSizingSections/sectionOrder";
import type {
  ScenarioExportRow,
  TargetExportUnits
} from "./ExportSizingSections/sizingExportTypes";
import { ApparentPowerUnits } from "@/libs/units/apparentPower";
import { ConcentrationUnits } from "@/libs/units/concentration";
import { CurrentUnits } from "@/libs/units/current";
import { DensityUnits, API, SpecificGravity } from "@/libs/units/density";
import { GasFlowRateUnits, LiquidFlowRateUnits } from "@/libs/units/flowRate";
import { ProductivityIndexUnits } from "@/libs/units/productivityIndex";
import { LargeLengthUnits, SmallLengthUnits } from "@/libs/units/length";
import { LiquidRatioUnits } from "@/libs/units/liquidRatio";
import { PressureUnits } from "@/libs/units/pressure";
import { SpeedUnits } from "@/libs/units/speed";
import { TemperatureUnits } from "@/libs/units/temperature";
import { ViscosityUnits } from "@/libs/units/viscosity";
import { MassUnits } from "@/libs/units/mass";
import { PowerUnits } from "@/libs/units/power";
import { VoltageUnits } from "@/libs/units/voltage";
import { VolumeUnits } from "@/libs/units/volume";
import type { ConvertableUnit } from "@/libs/units";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import { orderScenariosBySizingOrder } from "@/utils/scenarioUtils";

const props = defineProps({
  shouldExportSizing: {
    type: Boolean,
    required: true
  },
  sizings: {
    type: Array as PropType<Sizing[]>,
    required: true,
    validator: (arr: Sizing[]) => Array.isArray(arr) && arr.length > 0
  }
});

const emit = defineEmits<{
  (e: "update:shouldExportSizing", value: boolean): void;
}>();

const isDownloading = ref(false);

const baseSizing = computed(() => {
  return props.sizings[0];
});

const userUnits = useSizingUnits(baseSizing);

const exportOptions = reactive({
  filetype: "XLSX" as "XLSX" | "CSV",
  largeLengthUnit: userUnits.targetLargeLengthUnit.value,
  smallLengthUnit: userUnits.targetSmallLengthUnit.value,
  liquidFlowUnit: userUnits.targetLiquidFlowUnit.value,
  gasFlowUnit: userUnits.targetGasFlowUnit.value,
  productivityFlowUnit: userUnits.targetProductivityFlowUnit.value,
  pressureUnit: userUnits.targetPressureUnit.value,
  tempUnit: userUnits.targetTempUnit.value,
  densityUnit: userUnits.targetDensityUnit.value,
  viscosityUnit: userUnits.targetViscosityUnit.value,
  liquidRatioUnit: userUnits.targetLiquidRatioUnit.value,
  speedUnit: userUnits.targetSpeedUnit.value,
  massUnit: userUnits.targetMassUnit.value,
  currentUnit: userUnits.targetCurrentUnit.value,
  voltageUnit: userUnits.targetVoltageUnit.value,
  hydraulicPowerUnit: userUnits.targetHydraulicPowerUnit.value,
  realPowerUnit: userUnits.targetRealPowerUnit.value,
  apparentPowerUnit: userUnits.targetApparentPowerUnit.value,
  volumeUnit: userUnits.targetVolumeUnit.value,
  concentrationUnit: userUnits.targetConcentrationUnit.value
});

const dropdownOptions: {
  label: string;
  selectedUnit: keyof typeof exportOptions;
  unitsAvailable: ConvertableUnit[];
}[][] = [
  [
    {
      label: "Large Length",
      selectedUnit: "largeLengthUnit",
      unitsAvailable: LargeLengthUnits.options()
    },
    {
      label: "Small Length",
      selectedUnit: "smallLengthUnit",
      unitsAvailable: SmallLengthUnits.options()
    },
    {
      label: "Liquid Flow",
      selectedUnit: "liquidFlowUnit",
      unitsAvailable: LiquidFlowRateUnits.options()
    },
    {
      label: "Gas Flow",
      selectedUnit: "gasFlowUnit",
      unitsAvailable: GasFlowRateUnits.options()
    },
    {
      label: "Productivity Flow",
      selectedUnit: "productivityFlowUnit",
      unitsAvailable: ProductivityIndexUnits.options()
    },
    {
      label: "Liquid Ratio",
      selectedUnit: "liquidRatioUnit",
      unitsAvailable: LiquidRatioUnits.options()
    },
    {
      label: "Voltage",
      selectedUnit: "voltageUnit",
      unitsAvailable: VoltageUnits.options()
    },
    {
      label: "Hydraulic Power",
      selectedUnit: "hydraulicPowerUnit",
      unitsAvailable: PowerUnits.options()
    },
    {
      label: "Real Power",
      selectedUnit: "realPowerUnit",
      unitsAvailable: PowerUnits.options()
    },
    {
      label: "Concentration",
      selectedUnit: "concentrationUnit",
      unitsAvailable: ConcentrationUnits.options()
    }
  ],
  [
    {
      label: "Current",
      selectedUnit: "currentUnit",
      unitsAvailable: CurrentUnits.options()
    },
    {
      label: "Pressure",
      selectedUnit: "pressureUnit",
      unitsAvailable: PressureUnits.options()
    },
    {
      label: "Temperature",
      selectedUnit: "tempUnit",
      unitsAvailable: TemperatureUnits.options()
    },
    {
      label: "Density",
      selectedUnit: "densityUnit",
      unitsAvailable: DensityUnits.options([SpecificGravity, API])
    },
    {
      label: "Viscosity",
      selectedUnit: "viscosityUnit",
      unitsAvailable: ViscosityUnits.options()
    },
    {
      label: "Speed",
      selectedUnit: "speedUnit",
      unitsAvailable: SpeedUnits.options()
    },
    {
      label: "Mass",
      selectedUnit: "massUnit",
      unitsAvailable: MassUnits.options()
    },
    {
      label: "Apparent Power",
      selectedUnit: "apparentPowerUnit",
      unitsAvailable: ApparentPowerUnits.options()
    },
    {
      label: "Volume",
      selectedUnit: "volumeUnit",
      unitsAvailable: VolumeUnits.options()
    }
  ]
];

watchEffect(() => {
  if (props.shouldExportSizing) {
    Object.assign(exportOptions, {
      filetype: "XLSX",
      largeLengthUnit: userUnits.targetLargeLengthUnit.value,
      smallLengthUnit: userUnits.targetSmallLengthUnit.value,
      liquidFlowUnit: userUnits.targetLiquidFlowUnit.value,
      gasFlowUnit: userUnits.targetGasFlowUnit.value,
      productivityFlowUnit: userUnits.targetProductivityFlowUnit.value,
      pressureUnit: userUnits.targetPressureUnit.value,
      tempUnit: userUnits.targetTempUnit.value,
      densityUnit: userUnits.targetDensityUnit.value,
      viscosityUnit: userUnits.targetViscosityUnit.value,
      liquidRatioUnit: userUnits.targetLiquidRatioUnit.value,
      speedUnit: userUnits.targetSpeedUnit.value,
      massUnit: userUnits.targetMassUnit.value,
      currentUnit: userUnits.targetCurrentUnit.value,
      voltageUnit: userUnits.targetVoltageUnit.value,
      hydraulicPowerUnit: userUnits.targetHydraulicPowerUnit.value,
      realPowerUnit: userUnits.targetRealPowerUnit.value,
      apparentPowerUnit: userUnits.targetApparentPowerUnit.value,
      volumeUnit: userUnits.targetVolumeUnit.value,
      concentrationUnit: userUnits.targetConcentrationUnit.value
    });
  }
});

async function formatExport(): Promise<void> {
  if (isDownloading.value) return;
  isDownloading.value = true;

  try {
    await nextTick();
    await new Promise(r => setTimeout(r, 0)); // eslint-disable-line promise/param-names

    const targetUnits = { ...exportOptions } as TargetExportUnits;
    const wb = XLSX.utils.book_new();
    const validSizingTypes = props.sizings.every(
      s => s.pumping_type === baseSizing.value.pumping_type
    );
    // For the sizings dashboard, all must be the same
    if (!validSizingTypes) {
      throw new Error("All sizings must have the same pumping type to export.");
    }
    for (const s of props.sizings) {
      await processSizing(s, wb, targetUnits);
    }
    const fileStem =
      (props.sizings?.length > 1
        ? "Scenario_Export"
        : baseSizing.value.title) || "Sizing";

    const stamp = new Date().toLocaleDateString();

    XLSX.writeFile(
      wb,
      `${fileStem.substring(0, 50)} ${stamp}.${exportOptions.filetype.toLowerCase()}`
    );
  } catch (err) {
    console.error("Export failed:", err);
  } finally {
    isDownloading.value = false;
    emit("update:shouldExportSizing", false);
  }
}
async function processSizing(
  sizing: Sizing,
  workbook: XLSX.WorkBook,
  targetUnits: TargetExportUnits
): Promise<void> {
  const scenarios = orderScenariosBySizingOrder(sizing, sizing.scenarios);
  const isEspOnShore =
    sizing.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore;
  let maxPumps = 1;
  const maxCables = 3;
  const maxSeals = isEspOnShore ? 2 : 1;
  const maxIntakes = isEspOnShore ? 3 : 1;

  scenarios.forEach(sc => {
    maxPumps = Math.max(sc.equipment?.pumps_order?.length ?? 0, maxPumps);
  });

  const dataRows: ScenarioExportRow[] = [];

  for (const scenario of scenarios) {
    const ds = useDestructureScenario(scenario);
    const calcs = useScenarioCalculations(ds);
    const row = createScenarioRow(
      sizing,
      ds,
      calcs,
      targetUnits,
      maxPumps,
      maxCables,
      maxSeals,
      maxIntakes
    );
    dataRows.push(row);

    scenario.r_performance_evaluations?.forEach(perf => {
      const modifiedWellProps: WellProperties = {
        ...ds.wellProperties.value,
        __typename: "scenario_well_properties",
        well_test_water_rate_stb_day: perf.water_rate_std_barrel_day,
        well_test_gas_rate_mscf_day: perf.gas_rate_mscf_day,
        well_test_oil_rate_stb_day: perf.oil_rate_std_barrel_day
      };

      const modifiedParams: OperatingParameters = {
        ...ds.parameters.value,
        __typename: "scenario_parameters",
        target_liquid_rate_std_barrel_day:
          perf.oil_rate_std_barrel_day + perf.water_rate_std_barrel_day,
        bottom_casing_pressure_psia: perf.casing_pressure_psia,
        bottom_tubing_pressure_psia: perf.tubing_pressure_psia,
        pump_hz: perf.pump_hz
      };

      const modifiedPumps: XrefPumps = perf.r_xref_pumps.map(px => {
        const original = scenario.equipment?.r_xref_pumps.find(
          eq => eq.id === px.xref_pump_id
        );
        if (!original) {
          throw new Error("Performance-eval pump not found on scenario.");
        }
        return {
          ...original,
          head_correction_ratio: px.head_correction_ratio,
          rate_correction_ratio: px.rate_correction_ratio,
          brake_horsepower_correction_ratio:
            px.brake_horsepower_correction_ratio
        };
      });

      const modifiedDs: DestructuredScenario = {
        ...ds,
        wellProperties: ref(modifiedWellProps),
        parameters: ref(modifiedParams),
        pumps: ref(mapKeys(modifiedPumps, p => p.id))
      };

      const perfCalcs = useScenarioCalculations(modifiedDs);
      dataRows.push(
        createScenarioRow(
          sizing,
          modifiedDs,
          perfCalcs,
          targetUnits,
          maxPumps,
          maxCables,
          maxSeals,
          maxIntakes,
          true
        )
      );
    });
  }

  const maxCols = Math.max(...dataRows.map(r => r.length));
  const sheetData = Array.from({ length: maxCols }, (_, colIdx) => {
    const header =
      dataRows.find(r => r[colIdx] !== undefined)?.[colIdx] ||
      dataRows[0][colIdx];

    return [
      header.section,
      header.label,
      header.overrideLabel &&
      header.overrideLabel.labelToReplace === (header.targetUnit?.label || "")
        ? header.overrideLabel.newLabel
        : header.targetUnit?.shortLabel || header.targetUnit?.label || "",
      ...dataRows.map(r => {
        const cell = r[colIdx];
        let v = cell ? unref(cell.value) : "";
        if (typeof v === "string") return v;
        if (v === undefined || v === null || isNaN(v)) return "";
        if (header.targetUnit && !cell.disableConversion) {
          v = header.targetUnit.toThisUnit(v);
        }
        const dp = cell.decimalPlaceOverride ?? 2;
        return Math.round(v * 10 ** dp) / 10 ** dp;
      })
    ];
  });

  const sheet: Sheet = XLSX.utils.aoa_to_sheet(sheetData);
  Object.entries(sheet).forEach(([k, c]) => {
    if (!k.startsWith("!")) c.t = "s";
  });

  sheet["!cols"] = [
    { wch: Math.max(...dataRows[0].map(f => f.section.length)) },
    { wch: Math.max(...dataRows[0].map(f => f.label.length)) },
    {
      wch: Math.max(
        ...dataRows[0].map(
          f =>
            (f.overrideLabel &&
            f.overrideLabel.labelToReplace === (f.targetUnit?.label || "")
              ? f.overrideLabel.newLabel
              : f.targetUnit?.shortLabel || f.targetUnit?.label || ""
            ).length
        )
      )
    },
    ...dataRows.map(col => ({
      wch: Math.max(...col.map(r => String(r.value).length))
    }))
  ];

  sheet["!rows"] = new Array(dataRows[0].length + 3).fill({ hpx: 18 });
  sheet["!merges"] = [];
  let mergeStart = 0;
  for (let r = 1; r < sheetData.length; r++) {
    if (sheetData[r][0] !== sheetData[r - 1][0]) {
      sheet["!merges"].push({
        s: { r: mergeStart, c: 0 },
        e: { r: r - 1, c: 0 }
      });
      mergeStart = r;
    }
  }
  sheet["!merges"].push({
    s: { r: mergeStart, c: 0 },
    e: { r: sheetData.length - 1, c: 0 }
  });

  const sheetName = buildSheetName(
    sizing.title ?? "Sheet",
    workbook.SheetNames
  );
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
}

function createScenarioRow(
  sizing: Sizing,
  scenario: DestructuredScenario,
  calcs: ScenarioCalculations,
  targetUnits: TargetExportUnits,
  maxPumps: number,
  maxCables: number,
  maxSeals: number,
  maxIntakes: number,
  isPerfEval = false
): ScenarioExportRow {
  const isHPS = sizing.pumping_type === Sizing_Pumping_Type_Enum.Hps;
  return getExportSections(isHPS)
    .flatMap(renderColsFn =>
      renderColsFn({
        sizing,
        calcs,
        isPerfEval,
        maxCables,
        maxPumps,
        maxSeals,
        maxIntakes,
        scenario,
        targetUnits
      })
    )
    .filter(col => col.hidden === false || col.hidden === undefined)
    .map(col => {
      let val = col.value;
      if (val === undefined || val === null) {
        val = "";
      }
      if (String(val) === "NaN") {
        val = "";
      }
      return { ...col, value: val };
    });
}

function buildSheetName(rawTitle: string, existing: string[]): string {
  const MAX = 31;

  const clean = (rawTitle || "Sheet")
    .replace(/[\\/:*?\[\]]/g, "-") // eslint-disable-line no-useless-escape
    .replace(/\s+/g, " ")
    .trim();

  const base = clean.substring(0, MAX);
  let name = base;
  let i = 1;

  while (existing.some(n => n.toLowerCase() === name.toLowerCase())) {
    const suffix = `(${i})`;
    const room = MAX - suffix.length;
    name = `${base.substring(0, room)}${suffix}`;
    i += 1;
  }
  return name;
}
</script>
